import React from "react";
import { useTranslation } from "react-i18next";
import type {
  TransformedCategory,
  TransformedTag,
  TransformedBrand,
} from "../../../types/productApiTypes";

interface ProductMetaProps {
  sku: string;
  categories: TransformedCategory[];
  tags: TransformedTag[];
  brands: TransformedBrand[];
  shopUrl: string;
}

const ProductMeta: React.FC<ProductMetaProps> = ({
  sku,
  categories,
  tags,
  brands,
  shopUrl,
}) => {
  const { t } = useTranslation("common");

  const createLink = (
    item: TransformedCategory | TransformedTag | TransformedBrand,
    paramName: string
  ) => {
    if (item.link && typeof item.link === "string") {
      return <a href={item.link}>{item.name}</a>;
    } else if (paramName) {
      const baseUrl = shopUrl.endsWith("/") ? shopUrl : `${shopUrl}/`;
      return <a href={`${baseUrl}?${paramName}=${item.slug}`}>{item.name}</a>;
    } else {
      return <span>{item.name}</span>;
    }
  };

  return (
    <div className="product-meta">
      {sku && (
        <div className="product-sku">
          <span className="meta-label">{t("productDetails.sku")}</span>{" "}
          <span className="meta-value">{sku}</span>
        </div>
      )}
      {brands.length > 0 && (
        <div className="product-brands">
          <span className="meta-label">{t("productDetails.brands")} </span>{" "}
          <span className="meta-value">
            {brands.map((brand, index) => (
              <React.Fragment key={brand.id}>
                {createLink(brand, "brand")}
                {index < brands.length - 1 && ", "}
              </React.Fragment>
            ))}
          </span>
        </div>
      )}
      {categories.length > 0 && (
        <div className="product-categories">
          <span className="meta-label">{t("productDetails.categories")}</span>{" "}
          <span className="meta-value">
            {categories.map((category, index) => (
              <React.Fragment key={category.id}>
                {createLink(category, "category")}
                {index < categories.length - 1 && ", "}
              </React.Fragment>
            ))}
          </span>
        </div>
      )}
      {tags.length > 0 && (
        <div className="product-tags">
          <span className="meta-label">{t("productDetails.tags")}</span>{" "}
          <span className="meta-value">
            {tags.map((tag, index) => (
              <React.Fragment key={tag.id}>
                {createLink(tag, "productTag")}
                {index < tags.length - 1 && ", "}
              </React.Fragment>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProductMeta;
